import React, { useContext, useEffect, useState } from 'react';
import { Button } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { AppContext, GET_CONSTANTS, toast } from '@nile/components';
import { AddTechEndpoint } from '@nile/banyan/src/Pages/Protected/Configuration/Pricing';
import ContactVendorModal from '@nile/components/src/Buttons/ContactVendorButton';
import {
  AccountClient,
  GET_ALL_ACTIVE_ADDONS,
  UPDATE_ASSET_LICENSE_STATE,
  GET_CUSTOMER_SUBSCRIPTION,
  RMMClient
} from '@nile/graphql';

function ActivateUnlicensedAssetCell(props) {
  const { isBulkAction, records, setRecords, row, actionClick, column } = props;

  const intl = useIntl();
  const appContext = useContext(AppContext);
  const { currentLanguage, isSuperopsUser } = appContext;

  const [openBuyAddon, setOpenBuyAddon] = useState(false);
  const [addOnList, setAddOnList] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(false);

  const [currency, setCurrency] = useState('USD');
  const [tenure, setTenure] = useState('YEARLY');
  const [locale, setLocale] = useState('en-US');

  const isExceeded =
    appContext?.customerLicense?.data?.getCustomerLicenseExceededDetails
      ?.isEndpointLicenseCountExceeded;

  const getAllAddons = () => {
    AccountClient.query({
      query: GET_ALL_ACTIVE_ADDONS,
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data?.getAllActiveAddons) {
        setAddOnList(res.data.getAllActiveAddons);
      }
    });
  };

  const getCountryList = async () => {
    try {
      const country = await GET_CONSTANTS('country');
      return country;
    } catch {
      // error
    }
  };

  const getCustomerSubscription = () => {
    AccountClient.query({
      query: GET_CUSTOMER_SUBSCRIPTION,
      fetchPolicy: 'network-only'
    })
      .then(async (res) => {
        if (res?.data?.getCustomerSubscription) {
          setCurrentSubscription(res.data.getCustomerSubscription);
          setTenure(
            res.data.getCustomerSubscription?.currentPlanPrice?.duration ||
              'YEARLY'
          );
          if (
            res.data.getCustomerSubscription?.currentPlanPrice?.currency
              ?.currencyCode
          ) {
            const curr =
              res.data.getCustomerSubscription.currentPlanPrice.currency;
            setCurrency(curr.currencyCode || 'USD');
            const countryList = await getCountryList();
            setLocale(
              countryList?.filter(
                (country) => country.currency === curr.currencyCode
              )?.[0]?.isoCode
                ? `${currentLanguage.value || 'en'}-${
                    countryList?.filter(
                      (country) => country.currency === curr.currencyCode
                    )?.[0]?.isoCode
                  }`
                : 'en-US'
            );
          }
        }
      })
      .finally(() => {});
  };

  const activateAsset = () => {
    setLoading(true);
    let assetIds = [];
    if (isBulkAction && records?.totalRecords) {
      assetIds = records?.selectedRecords?.map((el) => el?.assetId) || [];
    } else if (row?.original?.assetId) {
      assetIds = [row.original.assetId] || [];
    }
    if (assetIds?.length === 0) {
      setLoading(false);
      return;
    }
    RMMClient.mutate({
      mutation: UPDATE_ASSET_LICENSE_STATE,
      variables: {
        assetIds: assetIds,
        operationalStatus: 'ACTIVE'
      }
    })
      .then((res) => {
        if (res?.data?.updateAssetLicenseState) {
          toast('success', 'common.addedSuccessfully');
          if (isBulkAction && actionClick) {
            actionClick('refreshListData');
          }
          if (isBulkAction && records?.totalRecords && setRecords) {
            setRecords([]);
          }
          if (column?.actions?.onComplete) {
            column?.actions?.onComplete();
          }
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getAllAddons();
    getCustomerSubscription();
  }, []);

  return (
    <div className="flex-center">
      <Button
        icon={<SVGIcon name="Resolution" size="sm" />}
        size={isBulkAction ? 'medium' : 'slim'}
        success
        loading={loading}
        onClick={() => {
          if (isExceeded) {
            setOpenBuyAddon((prevState) => !prevState);
          } else {
            activateAsset();
          }
        }}
      >
        <FormattedMessage id="app.common.add" />
      </Button>

      {isSuperopsUser ? (
        <AddTechEndpoint
          active={openBuyAddon}
          handleClose={() => {
            setOpenBuyAddon(false);
          }}
          subscription={currentSubscription}
          plan={currentSubscription?.currentPlanPrice?.plan}
          getCustomerSubscription={getCustomerSubscription}
          tenure={tenure}
          currency={currency}
          locale={locale}
          addOnList={addOnList}
          intl={intl}
          setTenure={setTenure}
        />
      ) : (
        <ContactVendorModal
          active={openBuyAddon}
          handleClose={setOpenBuyAddon}
        />
      )}
    </div>
  );
}

export default ActivateUnlicensedAssetCell;
